import { useMutation } from "@apollo/client";
import { createFileRoute, Outlet, useMatchRoute, Link } from "@tanstack/react-router";
import { Calendar, Table } from "lucide-react";
import { useState } from "react";
import { BookingForm } from "@/bookings/BookingForm";
import { toBookingInputFields } from "@/bookings/BookingForm/mappers";
import { createSupplyChainBooking } from "@/bookings/BookingForm/mutations";
import { type BookingFormSchema } from "@/bookings/BookingForm/validation";
import { bookingsChartQuery } from "@/bookings/BookingsChart";
import { supplyChainBookingsQuery } from "@/bookings/BookingsTable";
import { destinationsSummaryQuery } from "@/bookings/FeedyardSummaryDashboard/DestinationsTable";
import {
  sourcesSummaryQuery,
  destinationsTotalsQuery,
  sourcesTotalsQuery,
} from "@/bookings/FeedyardSummaryDashboard/queries";
import { PageHeader } from "@/components/PageHeader";
import { Tabs, TabsList, TabsTrigger } from "@/components/Tabs";
import {
  AlertDialog,
  AlertDialogDescription,
  AlertDialogHeader,
  AlertDialogTitle,
  AlertDialogContent,
} from "@/components/ui/alert-dialog";
import { Button } from "@/components/ui/button";
import { useActiveBusinessUnit } from "@/hooks/use-active-business-unit";
import { useToast } from "@/hooks/use-toast";
import { extractErrorMessages, extractPayloadErrorMessages, getErrorMessage } from "@/lib/utils/errors";

export const Route = createFileRoute("/(authed)/_layout/bookings/_tabbed")({
  component: RouteComponent,
});

function RouteComponent() {
  const matchRoute = useMatchRoute();
  const { activeBusinessUnitId } = useActiveBusinessUnit();
  const { toast } = useToast();
  const [isOpen, setIsOpen] = useState(false);

  const destinationsMatch = matchRoute({
    to: "/bookings/summary",
    search: { view: "destinations" },
    includeSearch: true,
    fuzzy: false,
  });

  const sourcesMatch = matchRoute({
    to: "/bookings/summary",
    search: { view: "sources" },
    includeSearch: true,
    fuzzy: false,
  });

  const bookingsMatch = matchRoute({
    to: "/bookings",
    fuzzy: false,
  });

  const isBookingsSummary = destinationsMatch || sourcesMatch;
  const activeTab = isBookingsSummary ? "summary" : "bookings";

  const [createBooking] = useMutation(createSupplyChainBooking, {
    refetchQueries: () => {
      if (destinationsMatch) {
        return [destinationsTotalsQuery, destinationsSummaryQuery];
      }

      if (sourcesMatch) {
        return [sourcesTotalsQuery, sourcesSummaryQuery];
      }

      if (bookingsMatch) {
        return [supplyChainBookingsQuery, bookingsChartQuery];
      }

      return [];
    },
  });

  const handleSubmit = async (values: BookingFormSchema) => {
    try {
      if (!activeBusinessUnitId) {
        toast({
          title: "Error",
          description: "Missing active business unit.",
          variant: "destructive",
        });

        return;
      }

      const result = await createBooking({
        variables: {
          input: {
            businessUnit: activeBusinessUnitId,
            ...toBookingInputFields(values),
          },
        },
      });

      const errors = result.errors || [];

      if (errors.length > 0) {
        toast({
          title: "Error creating booking",
          description: extractErrorMessages(errors),
          variant: "destructive",
        });

        return;
      }

      const payload = result.data?.createSupplyChainBooking;
      const payloadErrors = payload?.errors ?? [];

      // The booking can come back null without any GraphQL error, so the dialog stays
      // open with the user's values rather than claiming a booking that doesn't exist
      if (payloadErrors.length > 0 || !payload?.supplyChainBooking) {
        toast({
          title: "Error creating booking",
          description: extractPayloadErrorMessages(payloadErrors) || "The booking could not be created.",
          variant: "destructive",
        });

        return;
      }

      toast({
        variant: "default",
        title: "Booking created successfully!",
      });

      setIsOpen(false);
    } catch (error: unknown) {
      toast({
        variant: "destructive",
        title: "Uh oh! Something went wrong.",
        description: getErrorMessage(error) || "There was a problem with your request.",
      });
    }
  };

  return (
    <>
      <PageHeader showDivider={false}>
        <div className="w-full space-y-4">
          <div className="flex flex-col gap-4 md:flex-row md:items-center md:justify-between">
            <div className="flex items-center justify-between md:justify-start md:gap-6">
              <PageHeader.Title>Bookings</PageHeader.Title>
              <Button
                variant={"confirm"}
                onClick={() => {
                  setIsOpen(true);
                }}
                className="md:hidden"
              >
                Create Booking
              </Button>
            </div>

            <div className="flex items-center justify-between md:gap-6 w-full md:w-auto">
              <Tabs value={activeTab} className="w-full md:w-auto">
                <TabsList variant="primary" className="w-full md:w-auto">
                  <TabsTrigger asChild variant="primary" value="bookings" className="flex-1 md:flex-none">
                    <Link to="/bookings">
                      <Calendar className="w-4 h-4 mr-2" />
                      Bookings
                    </Link>
                  </TabsTrigger>
                  <TabsTrigger asChild variant="primary" value="summary" className="flex-1 md:flex-none">
                    <Link to="/bookings/summary" search={{ view: "destinations" }}>
                      <Table className="w-4 h-4 mr-2" />
                      Summary
                    </Link>
                  </TabsTrigger>
                </TabsList>
              </Tabs>

              <Button
                variant={"confirm"}
                onClick={() => {
                  setIsOpen(true);
                }}
                className="hidden md:block"
              >
                Create Booking
              </Button>
            </div>
          </div>
        </div>
      </PageHeader>
      <div className="space-y-4">
        <Outlet />
      </div>

      <AlertDialog open={isOpen} onOpenChange={setIsOpen}>
        <AlertDialogContent className="max-w-[800px] max-h-[90vh] overflow-y-auto">
          <AlertDialogHeader>
            <AlertDialogTitle>Create Booking</AlertDialogTitle>
            <AlertDialogDescription className="sr-only">Create Booking Form</AlertDialogDescription>
          </AlertDialogHeader>
          <BookingForm
            mode="create"
            onSubmit={handleSubmit}
            onCancel={() => {
              setIsOpen(false);
            }}
          />
        </AlertDialogContent>
      </AlertDialog>
    </>
  );
}
